/**
 * Simulated View Counter — Organic Growth Algorithm
 * 
 * Generates realistic "social proof" view counts by combining real views
 * with a deterministic simulated count based on the article's published_at date.
 * 
 * Key properties:
 * - Deterministic: same article always produces the same simulated count at a given time
 * - Logarithmic growth: fast initial growth, gradual slowdown
 * - Time-of-day weighting: more "views" during business hours
 * - Per-article seed: each article gets a unique but consistent random factor
 * - Popularity multiplier: real views boost simulated growth
 */

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

// Seeded pseudo-random (deterministic per article)
function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

export function getSimulatedViewCount(
  articleId: string,
  publishedAt: string | null,
  realViews: number = 0
): number {
  if (!publishedAt) return realViews;

  const publishDate = new Date(publishedAt);
  const now = new Date();
  const elapsedMs = now.getTime() - publishDate.getTime();

  // If article is in the future or just published (< 1 hour), return real views
  if (elapsedMs < 3600000) return Math.max(realViews, 1);

  const elapsedHours = elapsedMs / 3600000;
  const elapsedDays = elapsedHours / 24;

  // Per-article seed for consistent randomness
  const seed = hashCode(articleId);
  const articleFactor = 0.9 + seededRandom(seed) * 0.2; // 0.9 – 1.1

  // Target range: starts ~30-50, caps at ~120-489 simulated (before real views)
  // Per-article cap between 120 and 489
  const capSeed = seededRandom(seed + 1);
  const simulatedCap = 120 + Math.round(capSeed * 369); // 120–489

  // Starting base: 30-50 on day 1
  const startBase = 30 + Math.round(seededRandom(seed + 2) * 20); // 30–50

  // Logarithmic growth toward cap over ~7-10 days, then plateau
  // Using 1 - e^(-t/tau) curve for smooth approach to cap
  const tau = 2.5 + seededRandom(seed + 3) * 1.5; // 2.5–4 days time constant
  const growthProgress = 1 - Math.exp(-elapsedDays / tau);
  
  let baseSimulated = startBase + (simulatedCap - startBase) * growthProgress;

  // Apply per-article variance
  baseSimulated *= articleFactor;

  // Time-of-day micro-variation (deterministic per hour)
  const hourSeed = hashCode(`${articleId}-${now.getFullYear()}-${now.getMonth()}-${now.getDate()}-${now.getHours()}`);
  const hourVariation = 1 + (seededRandom(hourSeed) - 0.5) * 0.06; // ±3%

  baseSimulated *= hourVariation;

  // Cap the simulated portion
  baseSimulated = Math.min(Math.round(baseSimulated), simulatedCap);

  // Final: simulated base + real views on top
  return baseSimulated + realViews;
}

/**
 * Format view count for display (e.g., 1.2K, 15K)
 */
export function formatViewCount(count: number): string {
  if (count >= 10000) {
    return Math.round(count / 1000) + 'K';
  }
  if (count >= 1000) {
    return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return count.toLocaleString();
}
